import { useId, useMemo } from 'react'
import { clsx } from 'clsx'
import { Droplet } from 'lucide-react'
import { CurrencyFlag } from '@/components/currency/CurrencyFlag'
import type { MarketQuote } from './useMarkets'

type AssetLike = Pick<MarketQuote, 'id' | 'category' | 'symbol'>

const COIN_STYLES: Record<string, { bg: string; glyph: string }> = {
  BTC: { bg: 'bg-[#f7931a]', glyph: '₿' },
  ETH: { bg: 'bg-[#627eea]', glyph: 'Ξ' },
  SOL: { bg: 'bg-gradient-to-br from-[#9945ff] to-[#14f195]', glyph: 'S' },
}

export function AssetIcon({ asset, className = 'size-10' }: { asset: AssetLike; className?: string }) {
  const base = clsx('relative inline-flex shrink-0 items-center justify-center rounded-full', className)
  if (asset.category === 'fiat') {
    const [a, b] = asset.symbol.split('/')
    return (
      <span className={clsx(base)} aria-hidden>
        <CurrencyFlag code={a!} className="absolute top-0 start-0 size-[70%]" />
        <CurrencyFlag code={b!} className="absolute end-0 bottom-0 size-[70%] ring-2 ring-surface-1" />
      </span>
    )
  }
  if (asset.category === 'gold' || asset.category === 'silver') {
    const gold = asset.category === 'gold'
    return (
      <span
        aria-hidden
        className={clsx(
          base,
          'text-[0.75rem] font-bold ring-1 ring-white/20',
          gold
            ? 'bg-gradient-to-br from-[#fbe7b0] via-[#e8bd5c] to-[#9c7020] text-[#3a2604]'
            : 'bg-gradient-to-br from-[#f4f6fa] via-[#bfc6d4] to-[#7c8597] text-[#232a36]',
        )}
      >
        {gold ? 'Au' : 'Ag'}
      </span>
    )
  }
  if (asset.category === 'oil') {
    return (
      <span aria-hidden className={clsx(base, 'bg-[#11161f] text-white ring-1 ring-white/15')}>
        <Droplet className="size-[45%]" fill="currentColor" />
      </span>
    )
  }
  const coin = COIN_STYLES[asset.id] ?? { bg: 'bg-surface-3', glyph: asset.symbol[0] }
  return (
    <span aria-hidden className={clsx(base, coin.bg, 'text-[1.05rem] font-bold text-white')}>
      {coin.glyph}
    </span>
  )
}

export function Sparkline({
  data,
  className = 'h-9 w-24',
  trend,
}: {
  data: number[]
  className?: string
  trend: 'up' | 'down'
}) {
  const id = useId().replace(/:/g, '')
  const { line, area } = useMemo(() => {
    const w = 100
    const h = 32
    const min = Math.min(...data)
    const max = Math.max(...data)
    const span = max - min || 1
    const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 2 - ((v - min) / span) * (h - 4)] as const)
    const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)}`).join(' ')
    return { line, area: `${line} L${w} ${h} L0 ${h} Z` }
  }, [data])
  const color = trend === 'up' ? 'var(--up)' : 'var(--down)'
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className={clsx('ltr', className)} aria-hidden>
      <defs>
        <linearGradient id={`${id}-f`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id}-f)`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  )
}
